import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThreadsBatchParams, useThreadsBatch } from '@screentone/addon-auth-wrapper';
import { Button, IconPlusCircle, Typography, Wrapper } from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { InfiniteScrollWaypoint } from 'components/infinite-scroll-waypoint/InfiniteScrollWaypoint';
import { getPageLockThreadId } from 'contexts/page-lock/PageLockContext';
import { PagesDtoInput, useInfinitePagesDtoQuery } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { PagesList, PagesSearchBar } from './components';
import { SelectLayoutModal } from './components/select-layout-modal/SelectLayoutModal';

const PER_PAGE = 30;
const initialPages: string[] = [];

export const Pages = () => {
  const currentProperty = useConvertedProperty();
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const { data: publicationSettingsData } = usePublicationSettings();
  const allowCreateSectionPage = publicationSettingsData?.publicationSetting.allowCreateSectionPage;
  const isLayoutMvpEnabled = currentProperty === 'penta';
  const [pagesInput, setPagesInput] = useState<PagesDtoInput>({
    publicationKey: ''
  });
  const [selectLayoutOpen, setSelectLayoutOpen] = useState(false);

  const {
    data,
    isLoading: isLoad,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    fetchStatus
  } = useInfinitePagesDtoQuery(
    {
      pagesInput,
      paginationParams: { first: PER_PAGE, after: null }
    },
    {
      enabled: !!pagesInput.publicationKey && pagesInput.publicationKey.length > 0,
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagesDTO.pageInfo.hasNextPage) return false;
        return {
          paginationParams: {
            first: PER_PAGE,
            after: lastPage.pagesDTO.pageInfo.endCursor
          }
        };
      }
    }
  );

  const isLoading = isLoad && fetchStatus !== 'idle';

  const threadIds = useMemo(() => {
    if (hasNextPage || !data) return initialPages;

    return data.pages.flatMap((page) =>
      page.pagesDTO.edges.map((edge) => getPageLockThreadId(edge.node.metadata.allessehId))
    );
  }, [hasNextPage, data]);

  const batchParams: ThreadsBatchParams = useMemo(() => ({ threadIds, limit: 1, sort: 'DESC' }), [threadIds]);
  const { threadsData } = useThreadsBatch(batchParams);

  useEffect(() => {
    if (!currentProperty) {
      return;
    }

    setPagesInput((pagesInput) => ({ ...pagesInput, publicationKey: currentProperty }));
  }, [currentProperty]);

  const handlePartialPagesInputChange = (newSearchParams: Partial<PagesDtoInput>) => {
    setPagesInput((pagesInput) => ({ ...pagesInput, ...newSearchParams }));
  };

  const nextPageProps = { fetchNextPage, hasNextPage, isFetchingNextPage };

  return (
    <Wrapper margin={{ top: 'lg' }}>
      <Typography variant="header2" data-testid="pages-heading">
        All Pages
      </Typography>
      <PagesSearchBar pagesInput={pagesInput} onPartialPagesInputChange={handlePartialPagesInputChange} />
      {(isAdmin || isSuper) && allowCreateSectionPage && (
        isLayoutMvpEnabled ? (
          <Button tertiary icon={IconPlusCircle} onClick={() => setSelectLayoutOpen(true)}>
            New Page
          </Button>
        ) : (
          <Button tertiary icon={IconPlusCircle} componentEl={Link} to="pages/new">
            New Page
          </Button>
        )
      )}
      <InfiniteScrollWaypoint nextPageProps={nextPageProps} />
      {isLoading || !data ? <ResponsiveLoader /> : <PagesList pagePages={data.pages} initialThreadData={threadsData} />}
      {selectLayoutOpen && <SelectLayoutModal onDismiss={() => setSelectLayoutOpen(false)} />}
    </Wrapper>
  );
};
